import { HttpResponse } from '@angular/common/http';
import { Component, computed, inject, input, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import * as fromSeriousSystem from '@serious-system';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import * as fromGenerated from '../../../../../_generated';
import * as fromAnalytics from '../../../../../analytics';
import * as fromFeatureFlags from '../../../../../feature-flags';
import { getFilenameFromContentDisposition } from '../../../../shared';

@Component({
  selector: 'squadbox-message-quick-actions-export',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    fromSeriousSystem.SpinnerDirective,
    TranslateModule,
    MatTooltipModule,
  ],
  template: `
    <button
      sdIconButton
      variant="icon"
      color="neutral"
      size="xs"
      [sdDropdownMenuTrigger]="exportDropdown"
      [disabled]="isLoading()"
    >
      @if (isLoading()) {
        <div sdSpinner size="sm" color="primary"></div>
      } @else {
        <svg
          matTooltip="{{ 'CHAT.DROPDOWN_MENU.EXPORT' | translate }}"
          matTooltipPosition="above"
          sdUseUIBasicIcon="file-export"
        ></svg>
      }
    </button>

    <sd-dropdown-menu
      #exportDropdown
      [options]="exportDropdownMenuOptions()"
    ></sd-dropdown-menu>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class MessageQuickActionsExportComponent {
  private readonly featureFlagsStore =
    inject<Store<fromFeatureFlags.FeatureFlagsState>>(Store);
  private readonly translate = inject(TranslateService);
  private readonly toastrService = inject(ToastrService);
  private readonly chatsService = inject(fromGenerated.ChatsService);
  private readonly analyticsService = inject(fromAnalytics.AnalyticsService);

  public readonly ffExport = computed(() => {
    const ffQuickActions = this.featureFlagsStore.selectSignal(
      fromFeatureFlags.featureFlagsFeature.selectFeatureFlag<fromFeatureFlags.FeatureFlagQuickActions>(
        'ff-quick-actions',
      ),
    );
    return ffQuickActions().export;
  });
  public readonly chatId = input.required<number>();
  public readonly messageId = input.required<number>();
  public readonly isLoading = signal(false);
  public readonly exportDropdownMenuOptions = computed<
    fromSeriousSystem.DropdownMenuOption[]
  >(() => {
    const options: fromSeriousSystem.DropdownMenuOption[] = [];

    if (this.ffExport().docx) {
      options.push({
        label: this.translate.instant(
          'CHAT.DROPDOWN_MENU.EXPORT_TO_DOCX',
        ) as string,
        icon: { type: 'fileType', icon: 'word' },
        action: () => this.onExportToDocx(),
      });
    }

    return options;
  });

  public onExportToDocx(): void {
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);

    this.analyticsService
      .sendExportChatbotMessageEvent(this.chatId(), 'docx')
      .subscribe();

    this.chatsService
      .chatsControllerExportToDocxV1(
        this.chatId(),
        this.messageId(),
        'response',
      )
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          if (!response.body) {
            return;
          }
          const responseHeaders = response.headers;

          const contentDisposition = responseHeaders.get('Content-Disposition');
          const filename = getFilenameFromContentDisposition(
            contentDisposition,
            'export.docx', // if something goes wrong
          );

          const url = globalThis.URL.createObjectURL(response.body);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          globalThis.URL.revokeObjectURL(url);

          // Track file download event
          this.analyticsService
            .sendFileDownloadedEvent({
              from: 'export_docx',
            })
            .subscribe();
        }),
        catchError((error) => {
          this.toastrService.error(
            this.translate.instant(
              'CHAT.DROPDOWN_MENU.EXPORT_TO_DOCX_ERROR',
            ) as string,
            undefined,
            {
              closeButton: true,
              disableTimeOut: true,
            },
          );
          console.error(error);
          Sentry.captureException(error);
          this.isLoading.set(false);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe();
  }
}
